import type { WsClientPersistedState, WsClientPersistence } from '@xdocs/ws-protocol';
import { emptyPersistedState } from '@xdocs/ws-protocol';

const STORAGE_KEY = 'xdocs.ws-client-state';

/**
 * `localStorage`-backed {@link WsClientPersistence} (E20 §5.10-1/2/4): the
 * resume cursor, subscribed-channel set, tracked-job set, and offline queue
 * survive a full tab close-and-reopen, not just an in-memory blip.
 *
 * `@xdocs/ws-protocol` ships only the storage-agnostic interface + an
 * in-memory default (no `dom` lib in that package, §3 dependency direction)
 * — wiring a real browser adapter is `apps/web`'s job. IndexedDB (richer
 * durable storage for editor drafts/wizard state, §5.10-3) is the WS-PWA
 * workstream's deliverable; localStorage is sufficient for this narrow
 * slice of state (small, JSON-serializable, no blob content) and keeps this
 * shell dependency-free.
 */
/** Wipes the persisted WS client state (E20 §5.10-1/2/4 cursor/channels/
 * jobs/offline-queue) — called on logout so a stale offline-queue mutation
 * or resume cursor from one account never gets flushed/replayed against a
 * different account signing in on the same browser afterward. */
export function clearBrowserWsPersistence(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // best-effort — nothing further to do if storage is unavailable.
  }
}

export function createBrowserWsPersistence(): WsClientPersistence {
  return {
    load(): WsClientPersistedState {
      if (typeof window === 'undefined') return emptyPersistedState();
      try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw) return emptyPersistedState();
        const parsed = JSON.parse(raw) as Partial<WsClientPersistedState>;
        return {
          lastSeenOutboxId: parsed.lastSeenOutboxId ?? null,
          channels: Array.isArray(parsed.channels) ? parsed.channels : [],
          trackedJobIds: Array.isArray(parsed.trackedJobIds) ? parsed.trackedJobIds : [],
          offlineQueue: Array.isArray(parsed.offlineQueue) ? parsed.offlineQueue : [],
        };
      } catch {
        // Corrupt/unavailable storage must never crash boot — start clean.
        return emptyPersistedState();
      }
    },
    save(state: WsClientPersistedState): void {
      if (typeof window === 'undefined') return;
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      } catch {
        // Storage full/unavailable — persistence degrades to in-memory-only
        // for this session rather than throwing out of the WS client.
      }
    },
  };
}
